import { Box, DialogContent, DialogTitle, Drawer } from "@mui/material";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useListRulePresetsRulesPresetsGetQuery } from "api";
import { SelectInput } from "components/forms";
import { IRule } from "interfaces/rules.interface";
import { RuleType, RuleTypes } from "interfaces/ruleType";
import { getRulesTypes } from "utils/notificationUtils";

import { RuleAwakeForm } from "../forms/RuleAwakeForm";
import { RuleDeviceOfflineForm } from "../forms/RuleDeviceOfflineForm";
import { RuleMovementForm } from "../forms/RuleMovementForm";
import { RuleNoPresenceForm } from "../forms/RuleNoPresenceForm";
import { RuleRRDeviationForm } from "../forms/RuleRRDeviationForm";
import { RuleSoundLevelForm } from "../forms/RuleSoundLevelForm";

type TypeRuleEntity = "subject" | "zone";

type RuleDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  entityId: string;
  defaultValues: IRule | null;
  disabled: boolean;
  ruleEntityType: TypeRuleEntity;
};

export function RuleDialog({
  isOpen,
  onClose,
  entityId,
  defaultValues,
  disabled,
  ruleEntityType,
}: RuleDialogProps) {
  const { t } = useTranslation();

  const { control, watch } = useForm<{ ruleType: RuleType }>({
    defaultValues: {
      ruleType: defaultValues?.type,
    },
    mode: "onChange",
  });

  const ruleTypeWatch = watch("ruleType");

  const { data: rulePreset, isFetching } =
    useListRulePresetsRulesPresetsGetQuery(
      {
        ruleType: ruleTypeWatch,
        rulePresetType: "conditions",
      },
      {
        skip: !ruleTypeWatch,
      },
    );

  const presets = [
    ...(rulePreset?.data ?? []).map(({ id, name }) => ({
      id,
      name: t(name as "default"),
    })),
    { id: "", name: t("custom") },
  ];

  return (
    <Drawer open={isOpen} onClose={onClose} anchor={"right"}>
      <DialogTitle>
        {defaultValues ? t("notification_rule_details") : t("add_a_rule")}
      </DialogTitle>
      <DialogContent sx={{ width: "500px" }} dividers>
        <Box display={"flex"} flexDirection={"column"} gap={1} height={"100%"}>
          <SelectInput
            control={control}
            fieldName={"ruleType"}
            label={t("type")}
            options={getRulesTypes(ruleEntityType).map((rule) => ({
              id: rule,
              label: t(rule),
            }))}
            disabled={disabled}
            placeholder={t("select_rules_type")}
          />
          {!isFetching && (
            <>
              {ruleTypeWatch === RuleTypes.Awake && (
                <RuleAwakeForm
                  onClose={onClose}
                  entityId={entityId}
                  disabled={disabled}
                  presets={presets}
                  defaultValues={
                    defaultValues?.type === RuleTypes.Awake
                      ? defaultValues
                      : null
                  }
                />
              )}
              {ruleTypeWatch === RuleTypes.DeviceOffline && (
                <RuleDeviceOfflineForm
                  onClose={onClose}
                  entityId={entityId}
                  disabled={disabled}
                  presets={presets}
                  defaultValues={
                    defaultValues?.type === RuleTypes.DeviceOffline
                      ? defaultValues
                      : null
                  }
                />
              )}
              {ruleTypeWatch === RuleTypes.Movement && (
                <RuleMovementForm
                  onClose={onClose}
                  entityiD={entityId}
                  disabled={disabled}
                  presets={presets}
                  defaultValues={
                    defaultValues?.type === RuleTypes.Movement
                      ? defaultValues
                      : null
                  }
                />
              )}
              {ruleTypeWatch === RuleTypes.NoPresence && (
                <RuleNoPresenceForm
                  onClose={onClose}
                  entityId={entityId}
                  disabled={disabled}
                  presets={presets}
                  defaultValues={
                    defaultValues?.type === RuleTypes.NoPresence
                      ? defaultValues
                      : null
                  }
                />
              )}
              {ruleTypeWatch === RuleTypes.SoundLevel && (
                <RuleSoundLevelForm
                  onClose={onClose}
                  entityId={entityId}
                  disabled={disabled}
                  presets={presets}
                  defaultValues={
                    defaultValues?.type === RuleTypes.SoundLevel
                      ? defaultValues
                      : null
                  }
                />
              )}
              {ruleTypeWatch === RuleTypes.RRDeviation && (
                <RuleRRDeviationForm
                  onClose={onClose}
                  entityId={entityId}
                  disabled={disabled}
                  defaultValues={
                    defaultValues?.type === RuleTypes.RRDeviation
                      ? defaultValues
                      : null
                  }
                />
              )}
            </>
          )}
        </Box>
      </DialogContent>
    </Drawer>
  );
}
